import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";
import {useHistory} from "react-router-dom";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher, OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {doNotTranslateYet} from "@repo/i18n";
import {mediaQueries} from "@repo/shared-styles";

import type {
    deletePythonProgram as GqlResponse,
    deletePythonProgramVariables as GqlVariables,
} from "./__graphql-types__/deletePythonProgram.ts";

const deleteProgramMutation: GqlOperation<GqlResponse, GqlVariables> = gqlOp(
    gql`
        mutation deletePythonProgram($programId: ID!) {
            deleteProgram(programID: $programId) {
                error {
                    code
                    debugMessage
                }
            }
        }
    `,
);

type Props = {
    programId: string;
    parentCurationNodeUrl: string;
};

export default function DeleteButton({
    programId,
    parentCurationNodeUrl,
}: Props): React.ReactElement {
    const [deleteProgram, result] = useMutation(deleteProgramMutation);
    const history = useHistory();

    return (
        <ModalLauncher
            modal={({closeModal}) => (
                <DeleteModal
                    isDeleting={result?.status === "loading"}
                    onClose={closeModal}
                    onDelete={async () => {
                        const result = await deleteProgram({programId});
                        if (
                            result.status === "success" &&
                            result.data.deleteProgram != null &&
                            result.data.deleteProgram.error == null
                        ) {
                            history.push(parentCurationNodeUrl);
                            return true;
                        }
                        return false;
                    }}
                />
            )}
        >
            {({openModal}) => (
                <Button kind="secondary" onClick={openModal}>
                    {doNotTranslateYet`Delete`}
                </Button>
            )}
        </ModalLauncher>
    );
}

function DeleteModal({
    onDelete,
    isDeleting,
    onClose,
}: {
    onDelete: () => Promise<boolean>;
    isDeleting: boolean;
    onClose: () => void;
}): React.ReactElement {
    const [didMutationFail, setDidMutationFail] = React.useState(false);

    return (
        <OnePaneDialog
            style={styles.dialog}
            title={doNotTranslateYet`Delete program?`}
            content={
                <View style={styles.content}>
                    <Body>
                        {doNotTranslateYet`Are you sure you want to delete your program?`}
                    </Body>
                    <Body style={styles.error} role="alert">
                        {didMutationFail &&
                            doNotTranslateYet`We could not delete your program. Please try again.`}
                    </Body>
                </View>
            }
            footer={
                <View style={styles.footer}>
                    <Button
                        kind="tertiary"
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        {doNotTranslateYet`Cancel`}
                    </Button>
                    <Button
                        actionType="destructive"
                        disabled={isDeleting}
                        spinner={isDeleting}
                        onClick={async () => {
                            setDidMutationFail(false);
                            const didSucceed = await onDelete();
                            setDidMutationFail(!didSucceed);
                        }}
                    >
                        {doNotTranslateYet`Delete`}
                    </Button>
                </View>
            }
        />
    );
}

const styles = StyleSheet.create({
    dialog: {
        maxHeight: 280,
        [mediaQueries.smOrSmaller]: {
            maxHeight: 360,
        },
    },
    content: {
        display: "flex",
        flexDirection: "column",
        gap: spacing.medium_16,
    },
    error: {
        color: color.red,
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        gap: spacing.medium_16,
        [mediaQueries.smOrSmaller]: {
            flexBasis: "100%",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
        },
    },
});
